import type { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCellTimestamp } from '@/components/data-table/data-table-cell'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import { CopyButton } from '@/components/ui/copy-button'
import { formatPhoneForDisplay } from '@/utils/phone'
import type { MessageRecord } from '../types'
import { MessageDirectionBadge } from './MessageDirectionBadge'
import { messageBodyText } from './MessageStatus'

function Identifier({ value, label }: { value: string | null; label: string }) {
  if (!value) return <span className="text-muted-foreground">-</span>
  return (
    <div className="flex items-center gap-1">
      <TextWithTooltip text={value} className="max-w-[150px]" />
      <CopyButton
        content={value}
        label={`Copy ${label}`}
        onCopy={() => toast.success(`${label} copied to clipboard`)}
        onError={() => toast.error(`Failed to copy ${label}`)}
      />
    </div>
  )
}

// The feed is always newest-first; do not offer sorting of only the loaded page.
const columns: ColumnDef<MessageRecord>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Message ID" />
    ),
    enableHiding: false,
    cell: ({ row }) => (
      <Identifier value={row.original.id} label="Message ID" />
    ),
  },
  {
    accessorKey: 'duty_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Duty Name" />
    ),
    cell: ({ row }) =>
      row.original.duty_name ? (
        <TextWithTooltip
          text={row.original.duty_name}
          className="max-w-[250px]"
        />
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    filterFn: 'inDateRange',
    cell: ({ row }) => (
      <DataTableCellTimestamp date={row.original.created_at} />
    ),
  },
  {
    accessorKey: 'patient_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client Name" />
    ),
    cell: ({ row }) =>
      row.original.patient_name ? (
        <TextWithTooltip text={row.original.patient_name} />
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    accessorKey: 'phone',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone" />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatPhoneForDisplay(row.original.phone)}
      </span>
    ),
  },
  {
    accessorKey: 'body',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Message" />
    ),
    cell: ({ row }) => (
      <TextWithTooltip
        text={messageBodyText(row.original)}
        className="max-w-[360px]"
      />
    ),
  },
  {
    accessorKey: 'direction',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Direction" />
    ),
    cell: ({ row }) => (
      <MessageDirectionBadge direction={row.original.direction} />
    ),
  },
]

export const messagesColumns = columns.map((column) => ({
  ...column,
  enableSorting: false,
}))
